import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogState, FormGrid, SearchField } from '../../../primitives';
import { FormattedMessage } from 'react-intl';
import { useCMSSettingsContext } from '../../../CMS/Configuration/CMSSettingsContext';
import { CustomFieldsBlockElementType } from '../CustomFieldsBlock';
import { ConfigurableTableBlockElementType } from '../ConfigurableTableBlock/ConfigurableTableBlockElement';
import { List, ListItem, ListItemButton, ListItemText } from '@mui/material';

export type PreconfiguredBlockGrandType = (typeof CustomFieldsBlockElementType | typeof ConfigurableTableBlockElementType);

interface Props extends DialogState {
  doInsert: (grandType: PreconfiguredBlockGrandType, blockType: string, configuration: any) => void;
}

interface BlockOption {
  grand_type: PreconfiguredBlockGrandType;
  block_type: string;
  configuration: any;
}

export const InsertPreconfiguredBlockDialog = (props: Props) => {
  const [filter, setFilter] = useState<string>("");

  const cmsSettings = useCMSSettingsContext();

  useEffect(() => {
    if (props.isOpen) {
      cmsSettings.ensureLoaded();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  const availableBlocks = useMemo(() => {
    const cfbOptions: BlockOption[] = Object.keys(cmsSettings.settings.custom_fields_block_settings || {})
      .filter(k => !k.startsWith("__"))
      .map(k => ({
        block_type: k,
        grand_type: CustomFieldsBlockElementType,
        configuration: (cmsSettings.settings.custom_fields_block_settings || {})[k],
      }));
    const ctOptions: BlockOption[] = Object.keys(cmsSettings.settings.configurable_table_block_settings || {})
      .filter(k => !k.startsWith("__"))
      .map(k => ({
        block_type: k,
        grand_type: ConfigurableTableBlockElementType,
        configuration: (cmsSettings.settings.configurable_table_block_settings || {})[k],
      }));

    const all = [...cfbOptions, ...ctOptions];
    
    all.sort((a, b) => a.block_type.localeCompare(b.block_type));
    
    return all;
  }, [cmsSettings.settings.custom_fields_block_settings, cmsSettings.settings.configurable_table_block_settings]);

  const filteredBlocks = useMemo(() => {
    if (!filter) {
      return availableBlocks;
    }
    return availableBlocks.filter(b => b.block_type.toLowerCase().includes(filter.toLowerCase()));
  }, [availableBlocks, filter]);

  return (
    <Dialog
      dialogTitle={<FormattedMessage id="powerdoc.plugins.preconfigured_blocks.title" />}
      noFullscreen
      isOpen={props.isOpen}
      close={() => props.close()}
    >
      <FormGrid columns="1fr" noMargin>
        <SearchField
          filter={filter}
          setFilter={setFilter}
          noButton
          autoFocus
          fullWidth
          doSearch={() => {
            if(filteredBlocks.length === 1) {
              const block = filteredBlocks[0];
              props.doInsert(block.grand_type, block.block_type, block.configuration);
              props.close();
            }
          }}
        />

        <List sx={{ width: '100%', bgcolor: 'background.paper', maxHeight: 400, overflow: 'auto' }}>
          {filteredBlocks.map((block) => (
            <ListItem key={`${block.grand_type}-${block.block_type}`} disablePadding>
              <ListItemButton
                onClick={() => {
                  props.doInsert(block.grand_type, block.block_type, block.configuration);
                  props.close();
                }}
              >
                <ListItemText
                  primary={block.block_type}
                  secondary={block.grand_type}
                />
              </ListItemButton>
            </ListItem>
          ))}
          
          {filteredBlocks.length === 0 && (
            <ListItem>
              <ListItemText 
                primary={<FormattedMessage id="powerdoc.plugins.preconfigured_blocks.no_results" defaultMessage="No blocks found" />} 
                sx={{ textAlign: 'center', color: 'text.secondary' }} 
              />
            </ListItem>
          )}
        </List>

      </FormGrid>
    </Dialog>
  );
};
